/**
 * Finding a receipt in a photo and straightening it - pure functions on raw pixels, so the IO
 * modules only decode and encode: the phone's canvas (lib/scan/photo.ts) and, for emailed receipts
 * and re-scans, sharp in services/ai (lib/documents.ts), which imports this file.
 *
 * Receipts are paper: bright and nearly colourless. On a table, a tray or a car seat that sets
 * them apart well enough to find the sheet, take its four corners and undo the perspective. When
 * the background is white too (a tablecloth) nothing stands out, and the caller keeps the whole
 * photo - a missed crop costs looks, a wrong one would cost data.
 */

export type Point = { x: number; y: number };
/** Corners in reading order: top left, top right, bottom right, bottom left. */
export type Quad = [Point, Point, Point, Point];

/** An image as interleaved channels (1 = grey, 3 = RGB, 4 = RGBA), row by row. */
export type RawImage = { data: Uint8Array; width: number; height: number; channels: number };

export type Detection = { corners: Quad; coverage: number };

// A receipt lying on the table in a wide shot can be small; the quality check judges the result.
const MIN_COVERAGE = 0.015;
const MAX_COVERAGE = 0.92;

/** How paper-like each pixel is: bright and unsaturated scores high, colour is pushed down. */
export function paperness(image: RawImage): Uint8Array {
  const { data, width, height, channels } = image;
  const out = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i += 1) {
    const o = i * channels;
    if (channels < 3) {
      out[i] = data[o]!;
      continue;
    }
    const r = data[o]!;
    const g = data[o + 1]!;
    const b = data[o + 2]!;
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    const saturation = Math.max(r, g, b) - Math.min(r, g, b);
    out[i] = Math.round(saturation > 60 ? lum * 0.35 : lum);
  }
  return out;
}

/** Otsu's threshold: the grey level that best splits the histogram into two classes. */
export function otsu(values: Uint8Array): number {
  const histogram = new Array<number>(256).fill(0);
  for (const value of values) histogram[value]! += 1;
  const total = values.length;
  let sum = 0;
  for (let level = 0; level < 256; level += 1) sum += level * histogram[level]!;
  let sumBackground = 0;
  let weightBackground = 0;
  let best = 0;
  let threshold = 127;
  for (let level = 0; level < 256; level += 1) {
    weightBackground += histogram[level]!;
    if (weightBackground === 0) continue;
    const weightForeground = total - weightBackground;
    if (weightForeground === 0) break;
    sumBackground += level * histogram[level]!;
    const meanBackground = sumBackground / weightBackground;
    const meanForeground = (sum - sumBackground) / weightForeground;
    const between = weightBackground * weightForeground * (meanBackground - meanForeground) ** 2;
    if (between > best) {
      best = between;
      threshold = level;
    }
  }
  return threshold;
}

/** The pixels of the largest 4-connected region of `mask`, as indices. */
export function largestComponent(mask: Uint8Array, width: number, height: number): Int32Array {
  const labels = new Int32Array(width * height);
  const stack = new Int32Array(width * height);
  let bestLabel = 0;
  let bestSize = 0;
  let label = 0;
  for (let start = 0; start < mask.length; start += 1) {
    if (!mask[start] || labels[start]) continue;
    label += 1;
    let size = 0;
    let top = 0;
    stack[top++] = start;
    labels[start] = label;
    while (top > 0) {
      const index = stack[--top]!;
      size += 1;
      const x = index % width;
      const neighbours = [
        x > 0 ? index - 1 : -1,
        x < width - 1 ? index + 1 : -1,
        index - width,
        index + width,
      ];
      for (const next of neighbours) {
        if (next < 0 || next >= mask.length || !mask[next] || labels[next]) continue;
        labels[next] = label;
        stack[top++] = next;
      }
    }
    if (size > bestSize) {
      bestSize = size;
      bestLabel = label;
    }
  }
  const out = new Int32Array(bestSize);
  let k = 0;
  for (let index = 0; index < labels.length; index += 1)
    if (labels[index] === bestLabel) out[k++] = index;
  return out;
}

/** Convex hull (Andrew's monotone chain), counter-clockwise in image coordinates. */
export function convexHull(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (sorted.length < 3) return sorted;
  const cross = (o: Point, a: Point, b: Point) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Point[] = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper: Point[] = [];
  for (const p of [...sorted].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0)
      upper.pop();
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

export function polygonArea(points: Point[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    area += a.x * b.y - b.x * a.y;
  }
  return Math.abs(area) / 2;
}

/** Puts four points into reading order: top left, top right, bottom right, bottom left. */
export function orderCorners(points: Point[]): Quad {
  const bySum = [...points].sort((a, b) => a.x + a.y - (b.x + b.y));
  const byDiff = [...points].sort((a, b) => a.x - a.y - (b.x - b.y));
  return [bySum[0]!, byDiff[byDiff.length - 1]!, bySum[bySum.length - 1]!, byDiff[0]!];
}

/**
 * The quadrilateral of a hull. A receipt photographed at an angle is a trapezoid, and its four
 * extreme points (smallest/largest x+y and x-y) are its corners. Turned by more than ~30 degrees
 * those extremes collapse; then the smallest enclosing rectangle is the better guess.
 */
export function quadOf(hull: Point[]): Quad {
  const extremes = orderCorners([
    hull.reduce((a, b) => (b.x + b.y < a.x + a.y ? b : a)),
    hull.reduce((a, b) => (b.x - b.y > a.x - a.y ? b : a)),
    hull.reduce((a, b) => (b.x + b.y > a.x + a.y ? b : a)),
    hull.reduce((a, b) => (b.x - b.y < a.x - a.y ? b : a)),
  ]);
  const hullArea = polygonArea(hull);
  if (hullArea > 0 && polygonArea(extremes) >= hullArea * 0.85) return extremes;
  return orderCorners(minAreaRect(hull));
}

/** Smallest rectangle around a convex hull; one of its sides lies on a hull edge. */
export function minAreaRect(hull: Point[]): Point[] {
  let best: Point[] = hull.slice(0, 4);
  let bestArea = Number.POSITIVE_INFINITY;
  for (let i = 0; i < hull.length; i += 1) {
    const a = hull[i]!;
    const b = hull[(i + 1) % hull.length]!;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length === 0) continue;
    const ux = (b.x - a.x) / length;
    const uy = (b.y - a.y) / length;
    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    for (const p of hull) {
      const u = p.x * ux + p.y * uy;
      const v = -p.x * uy + p.y * ux;
      minU = Math.min(minU, u);
      maxU = Math.max(maxU, u);
      minV = Math.min(minV, v);
      maxV = Math.max(maxV, v);
    }
    const area = (maxU - minU) * (maxV - minV);
    if (area < bestArea) {
      bestArea = area;
      const at = (u: number, v: number) => ({ x: u * ux - v * uy, y: u * uy + v * ux });
      best = [at(minU, minV), at(maxU, minV), at(maxU, maxV), at(minU, maxV)];
    }
  }
  return best;
}

/**
 * Finds the receipt in a (downscaled) photo. Returns null when nothing paper-like stands out:
 * too small to be the subject, or so large that it is the background.
 */
export function detectReceipt(image: RawImage): Detection | null {
  const { width, height } = image;
  const paper = paperness(image);
  const threshold = otsu(paper);
  const mask = new Uint8Array(paper.length);
  let bright = 0;
  let brightSum = 0;
  let darkSum = 0;
  for (let i = 0; i < paper.length; i += 1) {
    const isPaper = paper[i]! > threshold;
    mask[i] = isPaper ? 1 : 0;
    if (isPaper) {
      bright += 1;
      brightSum += paper[i]!;
    } else darkSum += paper[i]!;
  }
  // Paper has to stand out clearly from what it lies on; a near-uniform photo splits on noise.
  const dark = paper.length - bright;
  if (bright === 0 || dark === 0 || brightSum / bright - darkSum / dark < 60) return null;

  const component = largestComponent(mask, width, height);
  const coverage = component.length / (width * height);
  if (coverage < MIN_COVERAGE || coverage > MAX_COVERAGE) return null;

  // Only the outline matters for the hull; inner pixels just cost time.
  const outline: Point[] = [];
  for (const index of component) {
    const x = index % width;
    const y = (index - x) / width;
    const edge =
      x === 0 ||
      y === 0 ||
      x === width - 1 ||
      y === height - 1 ||
      !mask[index - 1] ||
      !mask[index + 1] ||
      !mask[index - width] ||
      !mask[index + width];
    if (edge) outline.push({ x, y });
  }
  const hull = convexHull(outline);
  if (hull.length < 4) return null;
  const corners = quadOf(hull);
  // Touching three or more borders means the "paper" runs out of the frame: likely background.
  const touches = [
    corners.some((p) => p.x <= 1),
    corners.some((p) => p.y <= 1),
    corners.some((p) => p.x >= width - 2),
    corners.some((p) => p.y >= height - 2),
  ].filter(Boolean).length;
  if (touches >= 3) return null;
  return { corners, coverage };
}

export function scaleQuad(quad: Quad, factor: number): Quad {
  return quad.map((p) => ({ x: p.x * factor, y: p.y * factor })) as Quad;
}

/** Output size of a straightened quad: the longer of each pair of opposite sides. */
export function targetSize(quad: Quad): { width: number; height: number } {
  const [tl, tr, br, bl] = quad;
  const d = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
  return {
    width: Math.max(1, Math.round(Math.max(d(tl, tr), d(bl, br)))),
    height: Math.max(1, Math.round(Math.max(d(tl, bl), d(tr, br)))),
  };
}

/**
 * The projective transform taking the four `from` points onto the four `to` points, as the
 * 3x3 matrix [a b c; d e f; g h 1] flattened to eight numbers.
 */
export function homography(from: Quad, to: Quad): number[] {
  const rows: number[][] = [];
  for (let i = 0; i < 4; i += 1) {
    const { x, y } = from[i]!;
    const { x: u, y: v } = to[i]!;
    rows.push([x, y, 1, 0, 0, 0, -u * x, -u * y, u]);
    rows.push([0, 0, 0, x, y, 1, -v * x, -v * y, v]);
  }
  // Gaussian elimination with partial pivoting on the 8x9 augmented matrix.
  for (let col = 0; col < 8; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < 8; r += 1)
      if (Math.abs(rows[r]![col]!) > Math.abs(rows[pivot]![col]!)) pivot = r;
    [rows[col], rows[pivot]] = [rows[pivot]!, rows[col]!];
    const p = rows[col]![col]!;
    if (Math.abs(p) < 1e-12) throw new Error("Degenerate quadrilateral");
    for (let c = col; c < 9; c += 1) rows[col]![c]! /= p;
    for (let r = 0; r < 8; r += 1) {
      if (r === col) continue;
      const factor = rows[r]![col]!;
      for (let c = col; c < 9; c += 1) rows[r]![c]! -= factor * rows[col]![c]!;
    }
  }
  return rows.map((row) => row[8]!);
}

/**
 * Straightens the quad of a single-channel image into a width x height rectangle. Each output
 * pixel is looked up in the source through the inverse transform, with bilinear sampling.
 */
export function warpGray(
  source: { data: Uint8Array; width: number; height: number },
  quad: Quad,
  width: number,
  height: number,
): Uint8Array {
  const target: Quad = [
    { x: 0, y: 0 },
    { x: width - 1, y: 0 },
    { x: width - 1, y: height - 1 },
    { x: 0, y: height - 1 },
  ];
  const [a, b, c, d, e, f, g, h] = homography(target, quad) as [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const out = new Uint8Array(width * height);
  const { data, width: sw, height: sh } = source;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const w = g * x + h * y + 1;
      const sx = (a * x + b * y + c) / w;
      const sy = (d * x + e * y + f) / w;
      const x0 = Math.floor(sx);
      const y0 = Math.floor(sy);
      if (x0 < 0 || y0 < 0 || x0 >= sw - 1 || y0 >= sh - 1) {
        out[y * width + x] = 255;
        continue;
      }
      const fx = sx - x0;
      const fy = sy - y0;
      const i = y0 * sw + x0;
      const top = data[i]! * (1 - fx) + data[i + 1]! * fx;
      const bottom = data[i + sw]! * (1 - fx) + data[i + sw + 1]! * fx;
      out[y * width + x] = Math.round(top * (1 - fy) + bottom * fy);
    }
  }
  return out;
}

/**
 * Variance of the Laplacian: a standard sharpness measure. Text in focus gives strong
 * second derivatives; a shaken photo smears them out.
 */
export function sharpness(image: { data: Uint8Array; width: number; height: number }): number {
  const { data, width, height } = image;
  let sum = 0;
  let sumSquares = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      const value =
        4 * data[i]! - data[i - 1]! - data[i + 1]! - data[i - width]! - data[i + width]!;
      sum += value;
      sumSquares += value * value;
      count += 1;
    }
  }
  if (count === 0) return 0;
  const mean = sum / count;
  return sumSquares / count - mean * mean;
}

/** Rectangles to search for a small QR code: the whole image, then 2x2 and 3x3 overlapping tiles. */
export function qrTiles(
  width: number,
  height: number,
): Array<{ left: number; top: number; width: number; height: number }> {
  const tiles: Array<{ left: number; top: number; width: number; height: number }> = [];
  for (const grid of [2, 3]) {
    const tileW = Math.ceil((width / grid) * 1.4);
    const tileH = Math.ceil((height / grid) * 1.4);
    for (let row = 0; row < grid; row += 1) {
      for (let col = 0; col < grid; col += 1) {
        const left = Math.max(
          0,
          Math.min(width - tileW, Math.round((col * width) / grid - (tileW - width / grid) / 2)),
        );
        const top = Math.max(
          0,
          Math.min(height - tileH, Math.round((row * height) / grid - (tileH - height / grid) / 2)),
        );
        tiles.push({ left, top, width: Math.min(tileW, width), height: Math.min(tileH, height) });
      }
    }
  }
  return tiles;
}
